import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  Sphere,
  TorusKnot,
  Float,
  Environment,
  Stars,
  MeshDistortMaterial,
  Sparkles as ThreeSparkles,
} from "@react-three/drei";
import {
  ArrowRight,
  Globe,
  Package,
  Shield,
  CheckCircle,
  Users,
  Award,
  Utensils,
  ChevronRight,
  Truck,
  Loader2,
  SprayCan,
} from "lucide-react";
import { productService } from "../api/services/productService";

// ========== 3D SCENE ==========
const ThreeDScene = ({ darkMode }) => {
  const groupRef = useRef();
  const coreRef = useRef();
  const ring1Ref = useRef();
  const ring2Ref = useRef();
  const particlesRef = useRef([]);

  useFrame((state) => {
    const time = state.clock.getElapsedTime();

    if (groupRef.current) {
      groupRef.current.rotation.y = time * 0.005;
      groupRef.current.position.y = Math.sin(time * 0.15) * 0.02;
    }

    if (coreRef.current) {
      coreRef.current.rotation.y = time * 0.03;
      coreRef.current.scale.setScalar(1 + Math.sin(time * 1.2) * 0.01);
    }

    if (ring1Ref.current) {
      ring1Ref.current.rotation.x = time * 0.1;
      ring1Ref.current.rotation.z = time * 0.08;
    }

    if (ring2Ref.current) {
      ring2Ref.current.rotation.x = time * 0.08;
      ring2Ref.current.rotation.z = time * 0.12;
    }

    particlesRef.current.forEach((particle, i) => {
      if (particle) {
        const angle = time * 0.4 + i;
        particle.position.x = Math.cos(angle) * 2.2;
        particle.position.z = Math.sin(angle) * 2.2;
      }
    });
  });

  const primaryColor = darkMode ? "#60a5fa" : "#3b82f6";
  const accentColor = darkMode ? "#a78bfa" : "#8b5cf6";

  return (
    <group ref={groupRef}>
      <Float speed={0.8} floatIntensity={0.5}>
        <Sphere ref={coreRef} args={[1.1, 128, 128]} position={[0, 0, 0]}>
          <MeshDistortMaterial
            color={primaryColor}
            distort={0.04}
            speed={0.5}
            roughness={0.15}
            metalness={0.88}
            emissive={primaryColor}
            emissiveIntensity={0.12}
          />
        </Sphere>
      </Float>

      <Float speed={0.6} floatIntensity={0.3}>
        <TorusKnot
          ref={ring1Ref}
          args={[1.55, 0.035, 180, 24, 3, 3]}
          position={[0, 0, 0]}
        >
          <MeshDistortMaterial
            color={accentColor}
            metalness={0.92}
            roughness={0.06}
            emissive={accentColor}
            emissiveIntensity={0.2}
            transparent
            opacity={0.8}
          />
        </TorusKnot>
      </Float>

      <Float speed={0.5} floatIntensity={0.25}>
        <mesh ref={ring2Ref}>
          <torusGeometry args={[1.95, 0.02, 128, 200]} />
          <meshStandardMaterial
            color={primaryColor}
            emissive={primaryColor}
            emissiveIntensity={0.15}
            metalness={0.85}
            transparent
            opacity={0.5}
          />
        </mesh>
      </Float>

      {[...Array(16)].map((_, i) => (
        <mesh
          key={i}
          ref={(el) => (particlesRef.current[i] = el)}
          position={[2.1, 0, 0]}
        >
          <sphereGeometry args={[0.04, 16, 16]} />
          <meshStandardMaterial
            color={accentColor}
            emissive={accentColor}
            emissiveIntensity={0.5}
          />
        </mesh>
      ))}

      <ThreeSparkles
        count={2000}
        scale={[12, 12, 12]}
        size={0.04}
        speed={0.3}
        opacity={0.3}
        color={primaryColor}
      />

      <Stars radius={100} depth={50} count={3000} factor={3} fade speed={0.1} />

      <ambientLight intensity={0.5} />
      <pointLight position={[4, 4, 4]} intensity={0.7} />
      <pointLight position={[-2, 2, 3]} intensity={0.5} color={primaryColor} />
      <directionalLight position={[2, 3, 2]} intensity={0.6} />
      <Environment preset={darkMode ? "night" : "dawn"} background={false} />
    </group>
  );
};

// Helper function to get full image URL
const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/uploads")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}${path}`;
  }
  return path;
};

// Product Image Component
const ProductImage = ({ product, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      // mainImagePath
      if (product?.mainImagePath) {
        setImageUrl(getFullImageUrl(product.mainImagePath));
        setImageLoading(false);
        return;
      }

      // gallery images path
      if (product?.galleryImages && product.galleryImages.length > 0) {
        const firstImage = product.galleryImages[0];
        if (firstImage?.path) {
          setImageUrl(getFullImageUrl(firstImage.path));
          setImageLoading(false);
          return;
        }
        if (firstImage?.data) {
          setImageUrl(`data:${firstImage.mimeType};base64,${firstImage.data}`);
          setImageLoading(false);
          return;
        }
      }

      // Image URL
      if (product?.image) {
        setImageUrl(product.image);
        setImageLoading(false);
        return;
      }

      setImageLoading(false);
    };

    loadImage();
  }, [product]);

  if (imageLoading && !imageError) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={20} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={product?.imageAlt || product?.name || "Product"}
        className={`object-cover ${className}`}
        onError={() => {
          console.error(`Failed to load product image: ${imageUrl}`);
          setImageError(true);
          setImageLoading(false);
        }}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center ${className} bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900`}
    >
      <Package size={24} className="text-gray-400" />
    </div>
  );
};

// ========== CATEGORY CARD ==========
const CategoryCard = ({
  title,
  description,
  icon: Icon,
  link,
  delay = 0,
  category,
}) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  useEffect(() => {
    fetchProducts();
  }, [category]);

  useEffect(() => {
    if (products.length > 0) {
      const interval = setInterval(() => {
        setCurrentProductIndex(
          (prev) => (prev + 1) % Math.min(products.length, 5),
        );
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProductsByCategory(category);

      let productList = [];
      if (response?.data?.products) {
        productList = response.data.products;
      } else if (response?.products) {
        productList = response.products;
      } else if (Array.isArray(response?.data)) {
        productList = response.data;
      } else if (Array.isArray(response)) {
        productList = response;
      }

      setProducts(productList.slice(0, 6));
    } catch (error) {
      console.error(`Error fetching ${category} products:`, error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (productSlug) => {
    navigate(`/products/${productSlug}`);
  };

  const currentProduct = products[currentProductIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, type: "spring", stiffness: 100 }}
      whileHover={{ y: -6 }}
      className="group relative rounded-2xl overflow-hidden bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-white/50 dark:border-gray-800/50 shadow-xl"
    >
      {/* Gradient Accent Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-secondary-500" />

      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 dark:bg-primary-500/20 rounded-full">
            <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
              {title}
            </span>
          </div>
        </div>

        {/* Title & Description */}
        <Link to={`/categories/${category}`}>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer">
            {title}
          </h3>
        </Link>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">
          {description}
        </p>

        {/* Live Product Preview */}
        <div className="mb-5 bg-gray-100/50 dark:bg-gray-800/50 rounded-xl p-4">
          {loading ? (
            <div className="flex items-center justify-center h-16">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : currentProduct ? (
            <button
              onClick={() => handleProductClick(currentProduct.slug)}
              className="w-full flex items-center gap-3 group/product transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-200/70 hover:to-gray-100/70 dark:hover:from-gray-700/70 dark:hover:to-gray-800/70 rounded-xl p-2 -m-2"
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
                <ProductImage
                  product={currentProduct}
                  className="w-full h-full"
                />
              </div>
              <div className="flex-1 min-w-0 text-left">
                {/* Product Title */}
                <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1 group-hover/product:text-primary-600 dark:group-hover/product:text-primary-400 transition-colors">
                  {currentProduct.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {currentProduct.category === "food"
                    ? "Food & Beverages"
                    : "Non-Food Products"}{" "}
                  {/* • SKU: {currentProduct.sku} */}
                </p>
              </div>
              {/* Chevron Arrow */}
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 group-hover/product:translate-x-1 group-hover/product:text-primary-500 transition-all duration-300" />
            </button>
          ) : (
            <p className="text-sm text-gray-500 text-center">
              No products available
            </p>
          )}
        </div>

        {/* CTA Button */}
        <Link to={link}>
          <button className="w-full py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]">
            <span>Browse {title}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </Link>
      </div>
    </motion.div>
  );
};

// ========== FEATURE PILL ==========
const FeaturePill = ({ icon: Icon, label }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="flex items-center gap-2 px-4 py-2 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md rounded-full border border-white/50 dark:border-gray-700/50 shadow-sm"
    >
      <Icon className="w-3.5 h-3.5 text-primary-500" />
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
        {label}
      </span>
    </motion.div>
  );
};

// ========== MAIN HOME PAGE ==========
const HomePage = ({ darkMode = false }) => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 200], [1, 0.96]);
  const heroScale = useTransform(scrollY, [0, 200], [1, 0.99]);

  const features = [
    { icon: Shield, label: "Secure Trade" },
    { icon: CheckCircle, label: "Verified Partners" },
    { icon: Award, label: "Quality Assured" },
    { icon: Truck, label: "Global Logistics" },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* 3D Background */}
      <div className="fixed inset-0">
        <Canvas camera={{ position: [0, 0, 8], fov: 50 }}>
          <ThreeDScene darkMode={darkMode} />
        </Canvas>
      </div>

      {/* Main Content */}
      <motion.div
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="relative z-10 container mx-auto px-4 sm:px-6 py-8 md:py-16"
      >
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-white/50 dark:border-gray-700/50 shadow-sm mb-6"
          >
            <Globe className="w-3.5 h-3.5 text-primary-500" />
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 tracking-wide">
              Global Trade Solutions
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight"
          >
            Trade Smarter.
            <span className="block bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-500 bg-clip-text text-transparent">
              Grow Faster.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
          >
            Your trusted platform for global trade. Connect with verified
            suppliers, discover quality products, and scale your business across
            10+ Countries.
          </motion.p>
        </div>

        {/* Category Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16">
          <CategoryCard
            title="Food & Beverages"
            description="Premium quality food products sourced globally from trusted suppliers. From fresh produce to packaged goods."
            icon={Utensils}
            link="/categories/food"
            category="food"
            delay={0.1}
          />
          <CategoryCard
            title="Non-Food Products"
            description="Industrial supplies, consumer goods, electronics, and everything in between for your business needs."
            icon={SprayCan}
            link="/categories/non-food"
            category="non-food"
            delay={0.2}
          />
        </div>

        {/* Action Section */}
        <div className="max-w-4xl mx-auto">
          {/* Feature Pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {features.map((feature, idx) => (
              <FeaturePill
                key={idx}
                icon={feature.icon}
                label={feature.label}
              />
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all hover:scale-105"
            >
              <span>Browse our Catalog</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => navigate("/about")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md border border-white/50 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-sm hover:bg-white/80 dark:hover:bg-gray-700/80 transition-all"
            >
              <Users className="w-4 h-4" />
              <span>Learn More</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HomePage;
